// LeadAgentSellerPausedBanner.service.ts owns the HTTP call for the Seller-facing
// Lead Agent paused indicator. The endpoint is a bff-web aggregator that composes the
// acceptance state, leadbilling's enforcement verdict and the owner name into a
// Seller-safe payload (see bff-web workspace.leadAgentSellerStatus).

import { AxiosService } from 'src/axios/axios.service';
import type {
  tLeadAgentSellerStatus,
  tLeadAgentSellerStatusEnvelope,
} from './LeadAgentSellerPausedBanner.types';

export const LeadAgentSellerPausedBannerService = Object.freeze({
  /**
   * GET /workspace/{wid}/lead-agent/seller-status
   *
   * Returns the paused status only when the bot is paused AND the owner name is
   * present; anything else resolves to null so the caller hides the indicator. A
   * 404 (route/membership edge) is allowed to resolve rather than throw, mirroring
   * the acceptance service, so it does not pollute Sentry for the common case.
   */
  getSellerStatus: async function (
    workspaceId: string,
    signal?: AbortSignal,
  ): Promise<tLeadAgentSellerStatus | null> {
    const response = await AxiosService.get<tLeadAgentSellerStatusEnvelope>(
      `workspace/${workspaceId}/lead-agent/seller-status`,
      {
        isBFFService: true,
        signal,
        validateStatus: (status: number) =>
          (status >= 200 && status < 300) || status === 404,
      },
    );

    if (!response?.success || !response.data) return null;

    const { isPaused, ownerName, pauseReason } = response.data;
    // A paused indicator with no owner to name is not actionable; treat as hidden.
    if (!isPaused || !ownerName) return null;

    return { isPaused: true, ownerName, pauseReason: pauseReason ?? '' };
  },
});
